import type { ReactNode } from 'react';
import { Tag } from 'lucide-react';
import { usePromotions } from '@/hooks/useAppData';
import { PageHead } from '@/components/seo/PageHead';
import { useRouteLocale } from '@/hooks/useRouteLocale';
import { formatNumber } from '@/i18n/format';
import { useTranslate } from '@/i18n/LocaleLanguage';
import { promotionsListPageHead } from '@/seo/pageHeads';
import { catalogPath } from '@/seo/urls';
import { usePaginatedList } from '@/hooks/usePaginatedList';
import { useImpressionRef } from '@/hooks/useImpressionTracking';
import { PromotionCard } from '@/components/cards';
import {
  EmptyState,
  ErrorState,
  ListSkeleton,
  NoPageNotice,
  Pagination,
  PreviousPageLink,
  Spinner,
} from '@/components/ui';
import { Breadcrumbs } from '@/components/ui/Breadcrumbs';

/** Grid cell wrapper that registers the card for impression view-tracking. */
function TrackedCell({ id, children }: { id: string; children: ReactNode }) {
  const impressionRef = useImpressionRef('promotion', id);
  // The cell is the grid item and stretches to its row; `*:h-full` passes that
  // height on to the card, so every card in a row is as tall as the tallest.
  return (
    <div ref={impressionRef} className="h-full *:h-full">
      {children}
    </div>
  );
}

/** The first cards sit above the fold (two columns on a phone): their image is the LCP candidate, so it loads eagerly (audit F6). */
const LCP_CARDS = 2;

export function PromotionsPage() {
  // `?page=N` → page N's promotions and crawlable Prev/Next/number links
  // (usePaginatedList; infinite scroll is off by default there).
  const list = usePaginatedList(usePromotions);
  const { items, initialLoading, loadingMore, error, refetch, hasMore, sentinelRef, totalRecords } =
    list;

  const locale = useRouteLocale();
  // Page 1 of the list in the page's country; page N is `?page=N` (web plan W0.11, MW9).
  const basePath = catalogPath(locale, '/promotions');
  const t = useTranslate();
  // The count in the route locale's digits and grouping (web plan W3.0), not the browser's.
  const subtitle =
    typeof totalRecords === 'number'
      ? `${formatNumber(totalRecords, locale)} ${
          totalRecords === 1 ? 'promotion' : 'promotions'
        }`
      : 'The latest deals, all in one place';

  // Title, description and H1 from the formulas (web plan W0.6); "({Mon
  // YYYY})" only while a listed promotion is live.
  // Page N ≥ 2: " – Page N" and a self-canonical `?page=N`; a page that
  // does not exist is noindex (web plan W0.11). JSON-LD (W0.7): a
  // CollectionPage whose ItemList is this page's promotions, each named by
  // its H1 ("{Brand}: {headline}"). The SEO server builds the same head from
  // the same query (web plan W1.3).
  const head = promotionsListPageHead({
    items,
    totalRecords,
    page: list.page,
    seoPage: list.seo.page,
    t,
    locale,
  });
  return (
    <div className="mx-auto max-w-7xl px-4 py-6 md:py-8">
      <PageHead head={head} noindex={list.seo.noindex} />
      {/* Home › Promotions: the BreadcrumbList's own array (web plan W0.8). */}
      <Breadcrumbs crumbs={head.breadcrumbs} className="mb-4" />
      <div className="mb-6">
        <h1 className="text-2xl font-extrabold tracking-tight text-ink">{head.h1}</h1>
        <p className="mt-1 text-sm text-ink-secondary">{subtitle}</p>
      </div>

      {list.status !== 'ok' ? (
        <NoPageNotice basePath={basePath} requested={list.requested} />
      ) : initialLoading ? (
        <ListSkeleton count={10} />
      ) : error && items.length === 0 ? (
        <ErrorState message={error} onRetry={refetch} />
      ) : items.length === 0 ? (
        <EmptyState
          icon={Tag}
          title="No promotions yet"
          message="Check back soon for amazing deals."
        />
      ) : (
        <>
          <PreviousPageLink basePath={basePath} page={list.page} />
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {items.map((promotion, index) => (
              <TrackedCell key={promotion.id} id={promotion.id}>
                <PromotionCard promotion={promotion} variant="grid" priority={index < LCP_CARDS} headingLevel={2} />
              </TrackedCell>
            ))}
          </div>

          {error && !loadingMore && (
            <div className="mt-6 flex items-center justify-center gap-2 text-sm">
              <span className="text-ink-secondary">Couldn&apos;t load more</span>
              <button
                type="button"
                onClick={refetch}
                className="font-semibold text-primary-strong hover:underline"
              >
                Retry
              </button>
            </div>
          )}

          {/* Auto-load sentinel — the page grows as the user reaches the end.
              The next page is derived from the last successful response so a
              failed request doesn't permanently skip its page. */}
          {hasMore && (
            <div ref={sentinelRef} className="mt-8 flex justify-center py-4">
              {loadingMore && <Spinner size={24} className="text-ink-tertiary" />}
            </div>
          )}

          {/* Plain <a href> links to every page, for crawlers and for anyone
              who would rather page than scroll. */}
          <Pagination
            basePath={basePath}
            page={list.page}
            throughPage={list.throughPage}
            lastPage={list.lastPage ?? list.page}
            label="Promotion pages"
          />
        </>
      )}
    </div>
  );
}

export default PromotionsPage;
